import React, { useContext } from "react";
import { CardElement } from "@stripe/react-stripe-js";
import { Button, Dialog } from "components/ui";
import ParticleBackground from 'react-particle-backgrounds'
import { particleBackgroundSettings } from 'configs/particle-background.config';
import { HiChevronRight } from "react-icons/hi2";
import { Link } from "react-router-dom";
import { SessionContext } from "contexts/SessionContext";
import { PriorityModalContext } from "contexts/PriorityModalContext";
import { Trans } from "react-i18next";
import { TbCircleCheckFilled } from "react-icons/tb";
import { ModalContext } from "contexts/ModalContext";
import { FREE_TRIAL_DAYS, HAS_PAID_TRIAL, HAS_FREE_TRIAL, TRIAL_PRICE_DISPLAY } from "components/ui/utils/constant";

const CardDialog = ({ 
    cardDialogOpened, 
    setCardDialogOpened, 
    handleSubmitStripe, 
    isSubmittingCard, 
    subscriptionCreated, 
    className = "",
    handleStripeCheckout = undefined
}) => {

    const freeTrialdEndsAt = new Date().getTime() + 1000 * 3600 * 24 * FREE_TRIAL_DAYS;
    const { user } = useContext(SessionContext);
    const { closeModal } = useContext(ModalContext);
    const { closeModalPriority } = useContext(PriorityModalContext);

    const congratulationsMessage = user?.name
        ? `Thanks for subscribing, ${user.name}! You can start creating now.`
        : `Thanks for subscribing! You can start creating now.`;

    return (
        <Dialog
            isOpen={cardDialogOpened}
            onClose={() => setCardDialogOpened(false)}
            onRequestClose={() => setCardDialogOpened(false)}
            overlayClassName={`flex items-center justify-center w-full ${className} z-[20000000]`}
            contentClassName='relative !overflow-visible !overflow-y-visible px-6 lg:px-8'
            width={600}
        >
            {!user?.subscription ? (
                <h4 className="mb-2 mega-title !text-2xl mt-2">You are only one step away from your social media growth 🚀</h4>
            ) : (
                <h4 className="mb-2 mega-title !text-2xl mt-2">You are only one step away from you subscription upgrade 🚀</h4>
            )}
            <h6 className="text-gray-500 dark:text-gray-300 mb-6 !text-sm !font-normal">Add a credit or debit card.</h6>
            {!user?.subscription && !subscriptionCreated && <p className='mt-2 w-full block !text-sm mb-2'>
                {HAS_PAID_TRIAL && <>You'll be charged <b>{TRIAL_PRICE_DISPLAY} today</b> to start your {FREE_TRIAL_DAYS}-day trial. After the trial, your subscription renews at the regular price on {new Date(freeTrialdEndsAt).toLocaleDateString()}.<br /></>}
                {HAS_FREE_TRIAL && <>You won't be charged until {new Date(freeTrialdEndsAt).toLocaleDateString()}.<br /></>}
                You can cancel at any time.
            </p>}
            <form onSubmit={handleSubmitStripe}>
                <div className='py-8 mb-4 mt-4 px-4 rounded-2xl shadow-[0_0_30px_#0000002a]'>
                    <CardElement options={{ 
                        style: {
                            base: {
                                fontSize: '16px',
                                color: '#424770',
                                '::placeholder': {
                                    color: '#aab7c4',
                                },
                            }
                        }
                    }} />
                </div>
                {!!handleStripeCheckout && !subscriptionCreated &&
                    <div className="flex flex-col items-center justify-center mb-6 gap-2.5">
                        <span className="font-semibold text-tertiary-600">Or</span>
                        <div
                            className="!bg-[#6B71E3] hover:!brightness-[1.1] !text-white inline-block text-sm !p-6 !py-3 rounded-2xl cursor-pointer flex items-center justify-center gap-2"
                            onClick={handleStripeCheckout}
                        >
                            <img src="/img/others/stripe-s.webp" className="h-4 invert" />
                            <span>Checkout with <b>Stripe</b></span>
                        </div>
                    </div>
                }
                <hr className="-mx-6 lg:-mx-8" />
                <div className='flex flex-col lg:flex-row items-center justify-between mt-4 gap-8 -mb-2'>
                    <img src="/img/others/powered-by-stripe.png" className='h-7' />
                    <div className="flex gap-2 items-center">
                        <Button variant="plain" type="button" onClick={() => setCardDialogOpened(false)}>Close</Button>
                        <Button loading={isSubmittingCard} type="submit" variant="solid" size="md" className="flex justify-between items-center !px-4 gap-3 w-auto !text-sm">
                            {HAS_PAID_TRIAL && !user?.subscription
                                ? <span><Trans i18nKey={`subscription.startPaidTrial`}>Start {{ days: FREE_TRIAL_DAYS }}-Day Trial for {{ price: TRIAL_PRICE_DISPLAY }}</Trans></span>
                                : HAS_FREE_TRIAL && !user?.subscription
                                    ? <span><Trans i18nKey={`subscription.startFreeTrial`}>Start {{ days: FREE_TRIAL_DAYS }}-Day Free Trial</Trans></span>
                                    : <span>Confirm</span>
                            }
                            <HiChevronRight />
                        </Button>
                    </div>
                </div>
                {/* {!subscriptionCreated && <img src="/img/others/behind-wall-top.png" className='absolute hidden lg:inline-block top-0 translate-y-[-72%] -left-[7.5%] w-[45%] drop-shadow-xl' />} */}
            </form>

            <div className={`absolute left-0 top-0 w-full h-full bg-gradient-primary z-10 rounded-2xl px-6 transition-all duration-500 pointer-events-none opacity-0 ${subscriptionCreated && `!opacity-100 !pointer-events-auto`}`}>
                <ParticleBackground settings={particleBackgroundSettings} className="absolute pointer-events-none -ml-6" />
                <div className='relative z-[1] w-full h-full flex flex-col gap-8 items-center justify-center'>
                    <TbCircleCheckFilled className='text-8xl !text-green-600' />
                    <h4 className='text-white text-center'>{congratulationsMessage}</h4>
                    <Link to={`/home`} onClick={() => { closeModalPriority(); closeModal() }}>
                        <Button variant="twoTone">Start Creating</Button>
                    </Link>
                </div>
                <img src="/img/others/behind-wall-top.png" className='absolute top-0 translate-y-[-71.5%] left-[10%] w-[55%] drop-shadow-xl' />
            </div>
        </Dialog>
    )
}

export default CardDialog;